import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useWindowDimensions from '@/app/hooks/useWindowSize';
import { openConfirmModal } from '@/redux/reducers/cart-modal-reducer';
import { pageTypes, screenTypes } from '@/constants/types';
import { cn } from '@/utils/cn';
import {
  addCartItemsAsync,
  removeCartItemsAsync,
} from '@/redux/thunk/carts-thunk';
import { getStockAddableQuantity } from '@/utils/number';
import Icon from '../Icon';
import ButtonLabel from '../Typography/ButtonLabel';

function IncreaseDecreaseButton({
  currentProduct,
  isUnavailable = false,
  pageType = pageTypes.categoricalProduct,
  confirmModal = false,
}) {
  const dispatch = useDispatch();
  const windowDimensions = useWindowDimensions();
  const [currentStock, setCurrentStock] = useState(
    currentProduct?.limited_stock ? 0 : currentProduct?.max_cart_quantity,
  );
  const { items: cart, isHalt } = useSelector((state) => state.carts);
  const [productCount, setProductCount] = useState(0);

  useEffect(() => {
    const getProductCount = cart?.find(
      (item) => item.id === currentProduct?.id,
    );
    setProductCount(getProductCount ? getProductCount.cart_quantity : 0);
  }, [cart]);

  const handleIncreaseCartItem = (e, product) => {
    e.stopPropagation();
    e.preventDefault();

    if (currentStock <= 0) {
      return null;
    }

    if (productCount >= product?.max_cart_quantity) {
      return null;
    }
    const updatedProduct = {
      ...product,
      cart_quantity: productCount + 1,
    };
    dispatch(
      addCartItemsAsync({
        product: updatedProduct,
        type: 'increment',
      }),
    );
  };

  const handleDecreaseCartItem = (e, product) => {
    e.stopPropagation();
    e.preventDefault();

    if (productCount <= 1) {
      if (confirmModal) {
        dispatch(openConfirmModal({ type: 'item', data: product }));
      } else {
        dispatch(removeCartItemsAsync({ id: product?.id }));
      }
    } else {
      const updatedProduct = {
        ...product,
        cart_quantity: productCount - 1,
      };
      dispatch(
        addCartItemsAsync({
          product: updatedProduct,
          type: 'decrement',
        }),
      );
    }
  };

  const getIconSize = () => {
    if (pageType === pageTypes.productDetail) {
      return '36';
    }
    if (windowDimensions?.width < screenTypes.large) {
      return '24';
    }

    return '36';
  };

  useEffect(() => {
    const stockUpdate = getStockAddableQuantity({
      cart,
      productId: currentProduct?.id,
      maxQuantity: currentProduct?.max_cart_quantity,
      productData: currentProduct,
    });

    setCurrentStock(() => stockUpdate);
  }, [cart, currentProduct]);

  return (
    <div className="flex justify-center items-center gap-[6px]">
      <Icon
        type={productCount > 1 ? 'minus' : 'bin'}
        width={getIconSize()}
        height={getIconSize()}
        onClick={(e) => {
          if (isUnavailable) return null;

          handleDecreaseCartItem(e, currentProduct);
        }}
        className={cn('cursor-pointer', {
          'cursor-not-allowed': isUnavailable,
          'hover:cursor-not-allowed': isHalt,
        })}
      />

      <ButtonLabel
        label={productCount}
        className={cn(
          'text-senate font-bold px-24 border-2 border-senate rounded-[100px] py-10 h-[24px] lg:h-[36px] w-[54px] flex justify-center items-center',
          {
            'h-[40px] lg:h-[36px]': pageType === pageTypes.productDetail,
            'w-[40px] lg:w-[54px]': pageType === pageTypes.comparison,
          },
        )}
      />
      <Icon
        type="plus"
        onClick={(e) => {
          if (isUnavailable) return null;

          handleIncreaseCartItem(e, currentProduct);
        }}
        className={cn('cursor-pointer', {
          'cursor-not-allowed': isUnavailable,
          'hover:cursor-not-allowed': currentStock <= 0 ?? isHalt,
        })}
        width={getIconSize()}
        height={getIconSize()}
      />
    </div>
  );
}

export default IncreaseDecreaseButton;
